import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import MultiRangeSlider from "../inputs/MultiRangeSlider";
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FaTimes } from "react-icons/fa";
import CheckboxGroupScrollable from '../inputs/CheckboxGroupScrollable';
import { useAdmin } from '../../pages/admin/AdminContext';
import LabeledCheckbox from '../inputs/LabeledCheckBox';

Modal.setAppElement('#root');

const MemberFilterModal = ({ isOpen, onClose, initialFormData, onFormDataChange, onApply }) => {
    const [activeSemestersRange, setActiveSemestersRange] = useState(initialFormData?.activeSemestersRange || { min: 0, max: 16 });
    const [generalMeetingsRange, setGeneralMeetingsRange] = useState(initialFormData?.generalMeetingsRange || { min: 0, max: 200 });
    const [committeeMeetingsRange, setCommitteeMeetingsRange] = useState(initialFormData?.committeeMeetingsRange || { min: 0, max: 200 });
    const [volunteerEventsRange, setVolunteerEventsRange] = useState(initialFormData?.volunteerEventsRange || { min: 0, max: 200 });
    const [socialEventsRange, setSocialEventsRange] = useState(initialFormData?.socialEventsRange || { min: 0, max: 200 });

    const [activeSemestersResetKey, setActiveSemestersResetKey] = useState(0);
    const [generalMeetingsResetKey, setGeneralMeetingsResetKey] = useState(0);
    const [committeeMeetingsResetKey, setCommitteeMeetingsResetKey] = useState(0);
    const [volunteerEventsResetKey, setVolunteerEventsResetKey] = useState(0);
    const [socialEventsResetKey, setSocialEventsResetKey] = useState(0);

    const activeStatusTypes = React.useMemo(() => [
        { id: 1, activeStatusType: 'Active' },
        { id: 2, activeStatusType: 'Inactive' },
    ], []);

    const [selectedActiveStatusTypeIDs, setSelectedActiveStatusTypeIDs] = useState(
        initialFormData?.selectedActiveStatusTypeIDs || []
    );

    const gradYearTypes = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 6 }, (_, i) => ({
            id: i + 1,
            gradYearType: (currentYear + i).toString()
        }));
    }, []);

    const [selectedGradYearTypeIDs, setSelectedGradYearTypeIDs] = useState(
        initialFormData?.selectedGradYearTypeIDs || []
    );

    const { committees } = useAdmin();

    const committeeTypes = React.useMemo(() => [
        { id: 0, committeeType: 'None' },
        ...committees.map((committee) => ({
            id: committee.id,
            committeeType: committee.committeeName
        }))
    ], [committees]);

    const [selectedCommitteeTypeIDs, setSelectedCommitteeTypeIDs] = useState(
        initialFormData?.selectedCommitteeTypeIDs || []
    );

    const didInit = useRef(false);

    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        if (isOpen && !didInit.current) {
            setActiveSemestersRange(initialFormData?.activeSemestersRange || { min: 0, max: 16 });
            setGeneralMeetingsRange(initialFormData?.generalMeetingsRange || { min: 0, max: 200 });
            setCommitteeMeetingsRange(initialFormData?.committeeMeetingsRange || { min: 0, max: 200 });
            setVolunteerEventsRange(initialFormData?.volunteerEventsRange || { min: 0, max: 200 });
            setSocialEventsRange(initialFormData?.socialEventsRange || { min: 0, max: 200 });
            setSelectedActiveStatusTypeIDs(initialFormData?.selectedActiveStatusTypeIDs || []);
            setSelectedGradYearTypeIDs(initialFormData?.selectedGradYearTypeIDs || []);
            setSelectedCommitteeTypeIDs(initialFormData?.selectedCommitteeTypeIDs || []);
            didInit.current = true;
        } else if (!isOpen) {
            didInit.current = false;
        }
    }, [isOpen, initialFormData, activeStatusTypes, gradYearTypes, committeeTypes]);

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({
                activeSemestersRange,
                generalMeetingsRange,
                committeeMeetingsRange,
                volunteerEventsRange,
                socialEventsRange,
                selectedActiveStatusTypeIDs,
                selectedGradYearTypeIDs,
                selectedCommitteeTypeIDs,
            });
        }
    }, [activeSemestersRange, generalMeetingsRange, committeeMeetingsRange, volunteerEventsRange, socialEventsRange, selectedActiveStatusTypeIDs, selectedGradYearTypeIDs, selectedCommitteeTypeIDs]);

    const handleActiveStatusTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedActiveStatusTypeIDs((prev) => [...prev, changedValue]);
        } else {
            setSelectedActiveStatusTypeIDs((prev) =>
                prev.filter((id) => id !== changedValue)
            );
        }
    };

    const handleGradYearTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedGradYearTypeIDs((prev) => [...prev, changedValue]);
        } else {
            setSelectedGradYearTypeIDs((prev) =>
                prev.filter((id) => id !== changedValue)
            );
        }
    };

    const handleCommitteeTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedCommitteeTypeIDs((prev) => [...prev, changedValue]);
        } else {
            setSelectedCommitteeTypeIDs((prev) =>
                prev.filter((id) => id !== changedValue)
            );
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="activeStatusTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedActiveStatusTypeIDs.includes(value);
            });
        });
    }, [selectedActiveStatusTypeIDs]);

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="gradYearTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedGradYearTypeIDs.includes(value);
            });
        });
    }, [selectedGradYearTypeIDs]);

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="committeeTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedCommitteeTypeIDs.includes(value);
            });
        });
    }, [selectedCommitteeTypeIDs]);

    const handleActiveSemestersChange = useCallback(({ min, max }) => {
        setActiveSemestersRange({ min, max });
    }, []);

    const handleGeneralMeetingsChange = useCallback(({ min, max }) => {
        setGeneralMeetingsRange({ min, max });
    }, []);

    const handleCommitteeMeetingsChange = useCallback(({ min, max }) => {
        setCommitteeMeetingsRange({ min, max });
    }, []);

    const handleSocialEventsChange = useCallback(({ min, max }) => {
        setSocialEventsRange({ min, max });
    }, []);

    const handleVolunteerEventsChange = useCallback(({ min, max }) => {
        setVolunteerEventsRange({ min, max });
    }, []);

    const handleReset = () => {
        setActiveSemestersRange({ min: 0, max: 16 });
        setGeneralMeetingsRange({ min: 0, max: 200 });
        setCommitteeMeetingsRange({ min: 0, max: 200 });
        setVolunteerEventsRange({ min: 0, max: 200 });
        setSocialEventsRange({ min: 0, max: 200 });
        setActiveSemestersResetKey(prev => prev + 1);
        setGeneralMeetingsResetKey(prev => prev + 1);
        setCommitteeMeetingsResetKey(prev => prev + 1);
        setVolunteerEventsResetKey(prev => prev + 1);
        setSocialEventsResetKey(prev => prev + 1);
        setSelectedActiveStatusTypeIDs([]);
        setSelectedGradYearTypeIDs([]);
        setSelectedCommitteeTypeIDs([]);
    };

    const handleApply = () => {
        if (onApply) {
            onApply({
                activeSemestersRange,
                generalMeetingsRange,
                committeeMeetingsRange,
                volunteerEventsRange,
                socialEventsRange,
                selectedActiveStatusTypeIDs,
                selectedGradYearTypeIDs,
                selectedCommitteeTypeIDs,
            });
        }
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Member Filter Modal"
            className="custom-modal-content member-filter-modal"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className='d-flex flex-row justify-content-between align-items-center mb-3'>
                    <h2 className="text-start mb-0">Filter Members</h2>
                    <div className='d-flex flex-row justify-content-end align-items-center me-1'>
                        <button className="close-btn" style={{ marginTop: '-4px' }} onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className='d-flex flex-row mb-1'>
                    <div className='col-6'>
                        <MultiRangeSlider
                            key={`active-semesters-${activeSemestersResetKey}`}
                            min={0}
                            max={16}
                            initialMin={activeSemestersRange.min}
                            initialMax={activeSemestersRange.max}
                            onChange={handleActiveSemestersChange}
                            label='Active Semesters'
                        />
                    </div>
                    <MultiRangeSlider
                        key={`general-meetings-${generalMeetingsResetKey}`}
                        min={0}
                        max={200}
                        initialMin={generalMeetingsRange.min}
                        initialMax={generalMeetingsRange.max}
                        onChange={handleGeneralMeetingsChange}
                        label='General Meetings'
                    />
                </div>
                <div className='d-flex flex-row mb-1'>
                    <div className='col-6'>
                        <div style={{ width: '93%' }}>
                            <CheckboxGroupScrollable
                                id="active-status-types"
                                name="activeStatusTypesGroup"
                                label='Membership Status'
                                options={activeStatusTypes.map((as) => ({
                                    value: as.id,
                                    label: as.activeStatusType,
                                }))}
                                selectedValues={selectedActiveStatusTypeIDs}
                                onChange={handleActiveStatusTypeSelect}
                                onSelectAll={() =>
                                    setSelectedActiveStatusTypeIDs(activeStatusTypes.map((as) => as.id))
                                }
                                onUnselectAll={() => setSelectedActiveStatusTypeIDs([])}
                            />
                        </div>
                    </div>
                    <MultiRangeSlider
                        key={`committee-meetings-${committeeMeetingsResetKey}`}
                        min={0}
                        max={200}
                        initialMin={committeeMeetingsRange.min}
                        initialMax={committeeMeetingsRange.max}
                        onChange={handleCommitteeMeetingsChange}
                        label='Committee Meetings'
                    />
                </div>
                <div className='d-flex flex-row mb-1'>
                    <div className='col-6'>
                        <div style={{ width: '93%' }}>
                            <CheckboxGroupScrollable
                                id="grad-year-types"
                                name="gradYearTypesGroup"
                                label='Graduation Year'
                                options={gradYearTypes.map((gy) => ({
                                    value: gy.id,
                                    label: gy.gradYearType,
                                }))}
                                selectedValues={selectedGradYearTypeIDs}
                                onChange={handleGradYearTypeSelect}
                                onSelectAll={() => setSelectedGradYearTypeIDs(gradYearTypes.map((gy) => gy.id))}
                                onUnselectAll={() => setSelectedGradYearTypeIDs([])}
                            />
                        </div>
                    </div>
                    <MultiRangeSlider
                        key={`social-events-${socialEventsResetKey}`}
                        min={0}
                        max={200}
                        initialMin={socialEventsRange.min}
                        initialMax={socialEventsRange.max}
                        onChange={handleSocialEventsChange}
                        label='Social Events'
                    />
                </div>
                <div className='d-flex flex-row mb-1'>
                    <div className='col-6'>
                        <div style={{ width: '93%' }}>
                            <CheckboxGroupScrollable
                                id="committee-types"
                                name="committeeTypesGroup"
                                label='Committee(s)'
                                options={committeeTypes.map((ct) => ({
                                    value: ct.id,
                                    label: ct.committeeType,
                                }))}
                                selectedValues={selectedCommitteeTypeIDs}
                                onChange={handleCommitteeTypeSelect}
                                onSelectAll={() => setSelectedCommitteeTypeIDs(committeeTypes.map((ct) => ct.id))}
                                onUnselectAll={() => setSelectedCommitteeTypeIDs([])}
                            />
                        </div>
                    </div>
                    <MultiRangeSlider
                        key={`volunteer-events-${volunteerEventsResetKey}`}
                        min={0}
                        max={200}
                        initialMin={volunteerEventsRange.min}
                        initialMax={volunteerEventsRange.max}
                        onChange={handleVolunteerEventsChange}
                        label='Volunteer Events'
                    />
                </div>
                <div className='d-flex flex-row justify-content-center align-items-center two-button-container'>
                    <div className='me-3'>
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Apply"
                        action={handleApply}
                    />
                </div>
            </>
        </Modal>
    );
};

MemberFilterModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
    onApply: PropTypes.func
};

export default MemberFilterModal;
